import { useEnhancedSearch, SearchMode } from "@/hooks/useEnhancedSearch";
import { SearchIcon, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import capitalizeWords from "@/utils/helper/capitalizeWords";

const Search = () => {
  const {
    input,
    setInput,
    searchMode,
    setSearchMode,
    results,
    loading,
    getNavigationLink,
    brandsMap,
  } = useEnhancedSearch();
  const navigate = useNavigate();

  const handleResultClick = (result: any) => {
    setInput("");
    const link = getNavigationLink(result);
    navigate(link);
  };

  const searchModeOptions = [
    { value: "products", label: "Products" },
    { value: "brands", label: "Brands" },
    { value: "categories", label: "Categories" },
  ];

  const renderResultItem = (result: any) => {
    switch (result.type) {
      case "products":
        return (
          <div
            key={result.id}
            className="px-4 py-2 hover:bg-gray-100 rounded cursor-pointer transition mb-1"
            onClick={() => handleResultClick(result)}
          >
            <div className="font-medium">{result.name}</div>
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <span>{brandsMap?.[result?.brand_id]?.name}</span>
              <span className="text-gray-400">·</span>
              <span className="font-semibold text-base text-gray-900">
                ${result.price?.toFixed(2)}
              </span>
            </div>
          </div>
        );
      case "brands":
        return (
          <div
            key={result.id}
            className="px-4 py-2 hover:bg-gray-100 rounded cursor-pointer transition mb-1"
            onClick={() => handleResultClick(result)}
          >
            <div className="font-medium">{capitalizeWords(result.name)}</div>
            <div className="text-xs text-gray-500">Brand</div>
          </div>
        );
      case "categories":
        return (
          <div
            key={result.id}
            className="px-4 py-2 hover:bg-gray-100 rounded cursor-pointer transition mb-1"
            onClick={() => handleResultClick(result)}
          >
            <div className="font-medium">{capitalizeWords(result.name)}</div>
            <div className="text-xs text-gray-500">Category</div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <section className="relative z-10 -mt-20 mx-auto">
      <div className="container mx-auto lg:max-w-6xl px-4 md:max-w-3xl">
        <div className="bg-gradient-to-r from-white via-white to-[#F1F0FB] rounded-lg shadow-lg px-0 md:px-0 pt-2 md:p-6 transition-all duration-300 hover:shadow-xl">
          <div className="search-container transform transition-transform duration-300 relative w-full px-4">
            <form className="relative" onSubmit={(e) => e.preventDefault()}>
              <div className="flex items-center border-b border-gray-200">
                {/* Search Mode Selector */}
                <div className="w-44 mr-2 ">
                  <Select
                    value={searchMode}
                    onValueChange={(value: string) => setSearchMode(value as SearchMode)}
                  >
                    <SelectTrigger className="w-full h-11 border-none focus:ring-0 focus-visible:ring-0 bg-transparent">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {searchModeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          Search by {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Search Input */}
                <div className="flex-1 relative">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-all duration-300" />
                  <label
                    htmlFor="search-input"
                    className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                  >
                    Search
                  </label>
                  <Input
                    id="search-input"
                    className="flex h-11 rounded-md bg-transparent py-3 text-sm border-none outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 w-full pl-10 pr-10 focus:focus-visible:ring-0"
                    placeholder={`Search for ${searchMode}...`}
                    autoComplete="off"
                    spellCheck="false"
                    aria-label="Search"
                    type="search"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                  />
                  <Sparkles className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/10 animate-pulse" />
                </div>
              </div>
            </form>

            {input.trim() && (
              <div
                className="absolute left-0 top-14 z-50 overflow-y-auto overflow-x-hidden rounded-b-md bg-gradient-to-r from-white via-white to-[#F1F0FB] w-full px-4"
                role="listbox"
                aria-label="Suggestions"
              >
                {loading ? (
                  <div className="py-6 text-center text-sm text-gray-400" role="status">
                    Searching...
                  </div>
                ) : results.length === 0 ? (
                  <div className="py-6 text-center text-sm text-gray-400" role="status">
                    No {searchMode} found
                  </div>
                ) : (
                  <div>
                    <div className="text-xs text-gray-500 px-4 mb-1 mt-2">
                      {capitalizeWords(searchMode)}
                    </div>
                    <ScrollArea className="max-h-72">{results.map(renderResultItem)}</ScrollArea>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Search;
